import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, TextField, Button,
  Divider, Alert, CircularProgress, Snackbar,
  Chip, InputAdornment, Collapse, IconButton, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Menu, MenuItem,
  Table, TableBody, TableRow, TableCell, Tooltip, Popover
} from '@mui/material';
import {
  Save, ArrowBack, ExpandMore, ExpandLess,
  Warning, CheckCircle, CameraAlt, Navigation, AssignmentLate,
  CheckCircleOutline, NoteAdd, History, Sort, CloudDone, CloudUpload
} from '@mui/icons-material';
import TankLevelInput from '../components/TankLevelInput';
import PhotoCapture from '../components/PhotoCapture';
import useGps from '../hooks/useGps';
import useOfflineSync from '../hooks/useOfflineSync';
import pwaApi from '../services/pwaApi';
import * as offlineDb from '../services/offlineDb';

const MC_TO_LITERS = 4.15;

// Converti File → base64 per salvataggio in IndexedDB
async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Converti base64 → File per ripristino da IndexedDB
function base64ToFile(base64, fileName) {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], fileName, { type: mime });
}

export default function PlantReadingPage() {
  const { id: plantId } = useParams();
  const navigate = useNavigate();
  const { position } = useGps();
  const { isOnline, saveLocally } = useOfflineSync();

  const [plant, setPlant] = useState(null);
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Tank level
  const [tankLevel, setTankLevel] = useState(null);
  const initialTankLevelRef = useRef(null);

  // Plant-level photos (not linked to a specific meter)
  const [plantPhotos, setPlantPhotos] = useState([]);

  // Per-meter reading state: { [meterId]: { lettura, daFatturare, note, photos, expanded } }
  const [readings, setReadings] = useState({});

  // Intervention notes state
  const [interventionNotes, setInterventionNotes] = useState([]);

  // Confirmation dialog no longer needed for auto-submit
  const confirmDialog = { open: false, message: '', warnings: [] };

  // Notes dialog state
  const [noteDialog, setNoteDialog] = useState({ open: false, meterId: null });

  // History popover state
  const [historyAnchor, setHistoryAnchor] = useState(null);
  const [historyMeterId, setHistoryMeterId] = useState(null);

  // Sort state
  const [sortBy, setSortBy] = useState('progressivo');
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);

  // Draft state
  const [draftRestored, setDraftRestored] = useState(false);
  const draftTimerRef = useRef(null);
  const draftLoadedRef = useRef(false);

  // Auto-submit state (trasmissione automatica quando online)
  const [autoSyncStatus, setAutoSyncStatus] = useState(''); // '' | 'syncing' | 'synced' | 'error'
  const autoSubmittedRef = useRef({ readingIds: {}, photosUploaded: {}, tankSaved: false, plantPhotosSaved: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (isOnline) {
        const res = await pwaApi.getPlantMeters(plantId);
        const { plant: p, meters: m, insolutoMap: insMap, interventionNotes: notes } = res.data;
        setPlant(p);
        setMeters(m);
        if (notes) setInterventionNotes(notes);
        if (p.current_level_percent != null) {
          const lvl = Math.round(p.current_level_percent);
          setTankLevel(lvl);
          initialTankLevelRef.current = lvl;
        }
        // Init readings state
        const init = {};
        m.forEach(meter => {
          init[meter.id] = {
            lettura: '',
            daFatturare: true,
            tipoLettura: 'reale',
            note: '',
            photos: [],
            expanded: true
          };
        });
        setReadings(init);
      } else {
        // Offline: load from IndexedDB
        const cachedMeters = await offlineDb.getMetersByPlant(parseInt(plantId));
        const plants = await offlineDb.getPlants();
        const p = plants?.find(pl => pl.id === parseInt(plantId));
        setPlant(p || { id: parseInt(plantId), nome: `Impianto #${plantId}` });
        setMeters(cachedMeters || []);
        if (p?.current_level_percent != null) {
          const lvl = Math.round(p.current_level_percent);
          setTankLevel(lvl);
          initialTankLevelRef.current = lvl;
        }
        const init = {};
        (cachedMeters || []).forEach(meter => {
          init[meter.id] = {
            lettura: '',
            daFatturare: true,
            tipoLettura: 'reale',
            note: '',
            photos: [],
            expanded: true
          };
        });
        setReadings(init);
      }
    } catch (err) {
      setError('Errore caricamento dati impianto');
      // Fallback offline
      try {
        const cachedMeters = await offlineDb.getMetersByPlant(parseInt(plantId));
        if (cachedMeters?.length) {
          setMeters(cachedMeters);
          const init = {};
          cachedMeters.forEach(meter => {
            init[meter.id] = { lettura: '', daFatturare: true, tipoLettura: 'reale', note: '', photos: [], expanded: true };
          });
          setReadings(init);
        }
      } catch (_) {}
    } finally {
      setLoading(false);
    }
  }, [plantId, isOnline]);

  useEffect(() => { loadData(); }, [loadData]);

  // Ripristina draft salvato se presente
  useEffect(() => {
    if (loading || !meters.length || draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    (async () => {
      try {
        let draft = null;

        // Se online, prova prima dal server
        if (isOnline) {
          try {
            const res = await pwaApi.getDraft(plantId);
            if (res.data) draft = res.data;
          } catch (e) {
            console.warn('Errore caricamento draft dal server:', e);
          }
        }

        // Fallback: IndexedDB locale
        if (!draft) {
          draft = await offlineDb.getDraft(parseInt(plantId));
        }

        if (!draft) return;

        // Ripristina letture per contatore
        if (draft.readings) {
          setReadings(prev => {
            const restored = { ...prev };
            for (const [meterId, dr] of Object.entries(draft.readings)) {
              if (!restored[meterId]) continue;
              restored[meterId] = {
                ...restored[meterId],
                lettura: dr.lettura || '',
                daFatturare: dr.daFatturare !== false,
                tipoLettura: dr.tipoLettura || 'reale',
                note: dr.note || '',
                photos: (dr.photos || []).map(p => {
                  const file = base64ToFile(p.base64, p.name);
                  return { file, preview: URL.createObjectURL(file), name: p.name };
                }),
                expanded: restored[meterId].expanded
              };
            }
            return restored;
          });
        }

        if (draft.tankLevel != null) setTankLevel(draft.tankLevel);

        if (draft.plantPhotos?.length) {
          setPlantPhotos(draft.plantPhotos.map(p => {
            const file = base64ToFile(p.base64, p.name);
            return { file, preview: URL.createObjectURL(file), name: p.name };
          }));
        }

        setDraftRestored(true);
      } catch (err) {
        console.error('Errore ripristino draft:', err);
      }
    })();
  }, [loading, meters.length, plantId, isOnline]);

  // Auto-submit: quando online trasmette subito al server; quando offline salva draft locale
  useEffect(() => {
    if (loading || !meters.length || !draftLoadedRef.current) return;

    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(async () => {
      try {
        const hasData = Object.values(readings).some(r =>
          (r.lettura && r.lettura !== '') ||
          (r.photos && r.photos.length > 0) ||
          (r.note && r.note !== '')
        ) || plantPhotos.length > 0 || tankLevel !== initialTankLevelRef.current;

        if (!hasData) {
          await offlineDb.deleteDraft(parseInt(plantId));
          if (isOnline) {
            try { await pwaApi.deleteDraft(plantId); } catch (_) {}
          }
          return;
        }

        // === Se ONLINE: trasmetti immediatamente al server ===
        if (isOnline) {
          setAutoSyncStatus('syncing');
          try {
            // 1) Prepara letture da inviare
            const readingsBatch = [];
            for (const meter of meters) {
              const r = readings[meter.id];
              if (!r) continue;
              const hasLettura = r.lettura && r.lettura !== '';
              const hasPhotos = r.photos && r.photos.length > 0;
              if (!hasLettura && !hasPhotos) continue;

              if (hasLettura) {
                const lettura = parseFloat(r.lettura);
                if (isNaN(lettura)) continue;
                readingsBatch.push({
                  meter_id: meter.id,
                  nuova_lettura_mc: lettura,
                  data_lettura: new Date().toISOString().split('T')[0],
                  tipo_lettura: r.tipoLettura || 'reale',
                  da_fatturare: r.daFatturare !== false,
                  note: r.note || '',
                  gps_latitude: position?.latitude,
                  gps_longitude: position?.longitude,
                  gps_accuracy: position?.accuracy
                });
              } else if (hasPhotos && !autoSubmittedRef.current.readingIds[meter.id]) {
                // Foto senza lettura: crea reading tipo foto solo se non già creato
                readingsBatch.push({
                  meter_id: meter.id,
                  nuova_lettura_mc: null,
                  data_lettura: new Date().toISOString().split('T')[0],
                  tipo_lettura: 'foto',
                  da_fatturare: false,
                  note: '',
                  gps_latitude: position?.latitude,
                  gps_longitude: position?.longitude,
                  gps_accuracy: position?.accuracy
                });
              }
            }

            const tankLevelChanged = tankLevel !== initialTankLevelRef.current;
            const payload = {
              readings: readingsBatch,
              gps: position ? { latitude: position.latitude, longitude: position.longitude, accuracy: position.accuracy } : undefined
            };
            if (tankLevelChanged && tankLevel != null && !autoSubmittedRef.current.tankSaved) {
              payload.livello_impianto = { plant_id: parseInt(plantId), livello_percentuale: tankLevel };
            }

            // 2) Invia batch letture + livello
            if (readingsBatch.length > 0 || payload.livello_impianto) {
              const res = await pwaApi.saveBatchReadings(payload);
              const d = res.data;
              // Aggiorna readingIds
              if (d.readingIds) {
                autoSubmittedRef.current.readingIds = { ...autoSubmittedRef.current.readingIds, ...d.readingIds };
              }
              if (d.livelloAggiornato) {
                autoSubmittedRef.current.tankSaved = true;
              }
            }

            // 3) Upload foto contatori (solo quelle nuove)
            for (const meter of meters) {
              const r = readings[meter.id];
              if (!r?.photos?.length) continue;
              const alreadyUploaded = autoSubmittedRef.current.photosUploaded[meter.id] || 0;
              if (r.photos.length <= alreadyUploaded) continue;
              const newPhotos = r.photos.slice(alreadyUploaded);
              const readingId = autoSubmittedRef.current.readingIds[meter.id];
              try {
                const formData = new FormData();
                newPhotos.forEach(p => formData.append('photos', p.file));
                formData.append('plant_id', plantId);
                if (position?.latitude) formData.append('gps_latitude', position.latitude);
                if (position?.longitude) formData.append('gps_longitude', position.longitude);
                if (position?.accuracy) formData.append('gps_accuracy', position.accuracy);
                if (readingId) {
                  await pwaApi.uploadReadingPhotos(readingId, formData);
                } else {
                  await pwaApi.uploadMeterPhotos(meter.id, formData);
                }
                autoSubmittedRef.current.photosUploaded[meter.id] = r.photos.length;
              } catch (e) {
                console.error('Auto-upload foto contatore errore:', e);
              }
            }

            // 4) Upload foto impianto (solo quelle nuove)
            if (plantPhotos.length > autoSubmittedRef.current.plantPhotosSaved) {
              const newPlantPhotos = plantPhotos.slice(autoSubmittedRef.current.plantPhotosSaved);
              try {
                const formData = new FormData();
                newPlantPhotos.forEach(p => formData.append('photos', p.file));
                if (position?.latitude) formData.append('gps_latitude', position.latitude);
                if (position?.longitude) formData.append('gps_longitude', position.longitude);
                if (position?.accuracy) formData.append('gps_accuracy', position.accuracy);
                await pwaApi.uploadPlantPhotos(plantId, formData);
                autoSubmittedRef.current.plantPhotosSaved = plantPhotos.length;
              } catch (e) {
                console.error('Auto-upload foto impianto errore:', e);
              }
            }

            setAutoSyncStatus('synced');

            // Pulisci draft locale e server (i dati sono già stati trasmessi)
            await offlineDb.deleteDraft(parseInt(plantId));
            try { await pwaApi.deleteDraft(plantId); } catch (_) {}

          } catch (err) {
            console.error('Errore auto-submit:', err);
            setAutoSyncStatus('error');
            // Fallback: salva come draft offline
            await saveDraftLocally();
          }
        } else {
          // === OFFLINE: salva draft in IndexedDB ===
          await saveDraftLocally();
        }
      } catch (err) {
        console.error('Errore auto-save:', err);
      }
    }, 2000);

    async function saveDraftLocally() {
      try {
        const readingsToSave = {};
        for (const [meterId, r] of Object.entries(readings)) {
          const hasContent = (r.lettura && r.lettura !== '') || (r.photos && r.photos.length > 0) || (r.note && r.note !== '');
          if (!hasContent) continue;
          const photos = [];
          if (r.photos?.length) {
            for (const p of r.photos) {
              const base64 = await fileToBase64(p.file);
              photos.push({ base64, name: p.name });
            }
          }
          readingsToSave[meterId] = { lettura: r.lettura || '', daFatturare: r.daFatturare, tipoLettura: r.tipoLettura, note: r.note || '', photos };
        }
        const plantPhotosToSave = [];
        for (const p of plantPhotos) {
          const base64 = await fileToBase64(p.file);
          plantPhotosToSave.push({ base64, name: p.name });
        }
        await offlineDb.saveDraft(parseInt(plantId), { readings: readingsToSave, tankLevel, plantPhotos: plantPhotosToSave });
      } catch (e) {
        console.error('Errore salvataggio draft locale:', e);
      }
    }

    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [readings, tankLevel, plantPhotos, loading, meters.length, plantId, isOnline, meters, position, saveLocally]);

  const updateReading = (meterId, field, value) => {
    setReadings(prev => ({
      ...prev,
      [meterId]: { ...prev[meterId], [field]: value }
    }));
  };

  const getConsumption = (meter, letturaStr) => {
    const lettura = parseFloat(letturaStr);
    if (isNaN(lettura) || !meter.ultima_lettura) return null;
    const consumoMc = lettura - meter.ultima_lettura;
    if (consumoMc < 0) return { mc: consumoMc, litri: consumoMc * MC_TO_LITERS, anomaly: true };
    const avgMc = meter.consumo_medio_mc || 0;
    const isAnomaly = avgMc > 0 && consumoMc > avgMc * 2;
    return {
      mc: Math.round(consumoMc * 100) / 100,
      litri: Math.round(consumoMc * MC_TO_LITERS * 100) / 100,
      anomaly: isAnomaly || consumoMc < 0
    };
  };

  const isDone = (meterId) => {
    const r = readings[meterId];
    if (!r) return false;
    return (r.lettura && r.lettura !== '') || (r.photos && r.photos.length > 0);
  };

  const sortedMeters = useMemo(() => {
    return [...meters].sort((a, b) => {
      switch (sortBy) {
        case 'progressivo':
          return (a.sotto_zona || 0) - (b.sotto_zona || 0);
        case 'matricola':
          return (a.meter_number || '').localeCompare(b.meter_number || '');
        case 'letto': {
          const aDone = isDone(a.id) ? 1 : 0;
          const bDone = isDone(b.id) ? 1 : 0;
          return aDone - bDone;
        }
        default:
          return 0;
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meters, readings, sortBy]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const tankLevelChanged = tankLevel !== initialTankLevelRef.current;

      if (isOnline) {
        // Forza un ultimo invio immediato prima di uscire
        const readingsBatch = [];
        for (const meter of meters) {
          const r = readings[meter.id];
          if (!r) continue;
          const hasLettura = r.lettura && r.lettura !== '';
          const hasPhotos = r.photos && r.photos.length > 0;
          if (!hasLettura && !hasPhotos) continue;
          if (hasLettura) {
            const lettura = parseFloat(r.lettura);
            if (isNaN(lettura)) continue;
            readingsBatch.push({
              meter_id: meter.id, nuova_lettura_mc: lettura,
              data_lettura: new Date().toISOString().split('T')[0],
              tipo_lettura: r.tipoLettura || 'reale', da_fatturare: r.daFatturare !== false,
              note: r.note || '', gps_latitude: position?.latitude, gps_longitude: position?.longitude, gps_accuracy: position?.accuracy
            });
          } else if (hasPhotos) {
            readingsBatch.push({
              meter_id: meter.id, nuova_lettura_mc: null,
              data_lettura: new Date().toISOString().split('T')[0],
              tipo_lettura: 'foto', da_fatturare: false, note: '',
              gps_latitude: position?.latitude, gps_longitude: position?.longitude, gps_accuracy: position?.accuracy
            });
          }
        }

        const payload = { readings: readingsBatch, gps: position ? { latitude: position.latitude, longitude: position.longitude, accuracy: position.accuracy } : undefined };
        if (tankLevelChanged && tankLevel != null) {
          payload.livello_impianto = { plant_id: parseInt(plantId), livello_percentuale: tankLevel };
        }
        if (readingsBatch.length > 0 || payload.livello_impianto) {
          const res = await pwaApi.saveBatchReadings(payload);
          const d = res.data;
          if (d.readingIds) {
            autoSubmittedRef.current.readingIds = { ...autoSubmittedRef.current.readingIds, ...d.readingIds };
          }
        }

        // Upload foto non ancora caricate
        for (const meter of meters) {
          const r = readings[meter.id];
          if (!r?.photos?.length) continue;
          const alreadyUploaded = autoSubmittedRef.current.photosUploaded[meter.id] || 0;
          if (r.photos.length <= alreadyUploaded) continue;
          const newPhotos = r.photos.slice(alreadyUploaded);
          const readingId = autoSubmittedRef.current.readingIds[meter.id];
          try {
            const formData = new FormData();
            newPhotos.forEach(p => formData.append('photos', p.file));
            formData.append('plant_id', plantId);
            if (position?.latitude) formData.append('gps_latitude', position.latitude);
            if (position?.longitude) formData.append('gps_longitude', position.longitude);
            if (position?.accuracy) formData.append('gps_accuracy', position.accuracy);
            if (readingId) await pwaApi.uploadReadingPhotos(readingId, formData);
            else await pwaApi.uploadMeterPhotos(meter.id, formData);
          } catch (e) { console.error('Errore upload foto finale:', e); }
        }
        if (plantPhotos.length > autoSubmittedRef.current.plantPhotosSaved) {
          const newPP = plantPhotos.slice(autoSubmittedRef.current.plantPhotosSaved);
          try {
            const formData = new FormData();
            newPP.forEach(p => formData.append('photos', p.file));
            if (position?.latitude) formData.append('gps_latitude', position.latitude);
            if (position?.longitude) formData.append('gps_longitude', position.longitude);
            if (position?.accuracy) formData.append('gps_accuracy', position.accuracy);
            await pwaApi.uploadPlantPhotos(plantId, formData);
          } catch (e) { console.error('Errore upload foto impianto finale:', e); }
        }

        await offlineDb.deleteDraft(parseInt(plantId));
        try { await pwaApi.deleteDraft(plantId); } catch (_) {}
        setSuccess('Completato');
        setTimeout(() => navigate('/letture'), 800);
      } else {
        // Offline: salva tutto in blocco per sync successiva
        const pwaUser = JSON.parse(localStorage.getItem('pwa_user') || '{}');
        const readingsToSave = [];
        for (const meter of meters) {
          const r = readings[meter.id];
          if (!r) continue;
          const hasLettura = r.lettura && r.lettura !== '';
          const hasPhotos = r.photos && r.photos.length > 0;
          if (!hasLettura && !hasPhotos) continue;
          if (hasLettura) {
            const lettura = parseFloat(r.lettura);
            if (!isNaN(lettura)) {
              readingsToSave.push({
                meter_id: meter.id, nuova_lettura_mc: lettura,
                data_lettura: new Date().toISOString().split('T')[0],
                tipo_lettura: r.tipoLettura || 'reale', da_fatturare: r.daFatturare !== false,
                note: r.note || '', gps_latitude: position?.latitude, gps_longitude: position?.longitude, gps_accuracy: position?.accuracy
              });
            }
          } else if (hasPhotos) {
            readingsToSave.push({
              meter_id: meter.id, nuova_lettura_mc: null,
              data_lettura: new Date().toISOString().split('T')[0],
              tipo_lettura: 'foto', da_fatturare: false, note: '',
              gps_latitude: position?.latitude, gps_longitude: position?.longitude, gps_accuracy: position?.accuracy
            });
          }
          // Salva foto contatore in pendingPhotos
          if (hasPhotos) {
            for (const p of r.photos) {
              const base64 = await fileToBase64(p.file);
              await offlineDb.savePendingPhoto({
                plant_id: parseInt(plantId), meter_id: meter.id, tipo: 'contatore',
                base64, filename: p.name || p.file.name,
                gps_latitude: position?.latitude || null, gps_longitude: position?.longitude || null,
                gps_accuracy: position?.accuracy || null
              });
            }
          }
        }
        // Salva foto impianto in pendingPhotos
        for (const p of plantPhotos) {
          const base64 = await fileToBase64(p.file);
          await offlineDb.savePendingPhoto({
            plant_id: parseInt(plantId), meter_id: null, tipo: 'impianto',
            base64, filename: p.name || p.file.name,
            gps_latitude: position?.latitude || null, gps_longitude: position?.longitude || null,
            gps_accuracy: position?.accuracy || null
          });
        }
        await saveLocally({
          plantId: parseInt(plantId), plantName: plant?.nome,
          operatore: pwaUser.username || pwaUser.nome || '',
          readings: readingsToSave,
          livello_impianto: (tankLevelChanged && tankLevel != null) ? { plant_id: parseInt(plantId), livello_percentuale: tankLevel } : undefined,
          gps: position ? { latitude: position.latitude, longitude: position.longitude, accuracy: position.accuracy } : undefined,
          timestamp: new Date().toISOString()
        });
        await offlineDb.deleteDraft(parseInt(plantId));
        setSuccess('Letture e foto salvate offline - verranno sincronizzate');
        setTimeout(() => navigate('/letture'), 1500);
      }
    } catch (err) {
      console.error('handleSave ERROR:', err);
      setError(err.response?.data?.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const tankLevelChanged = tankLevel !== initialTankLevelRef.current;
  const filledCount = Object.values(readings).filter(r => (r.lettura && r.lettura !== '') || (r.photos && r.photos.length > 0)).length;
  const canSave = filledCount > 0 || plantPhotos.length > 0 || tankLevelChanged;

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Caricamento...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 12 }}>
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" sx={{ color: 'white' }} onClick={() => navigate('/letture')}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {plant?.plant_code || plant?.nome || `Impianto #${plantId}`}{plant?.citta ? ` — ${plant.citta}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {plant?.indirizzo || ''}
            </Typography>
          </Box>
          {plant?.latitude && plant?.longitude && (
            <IconButton
              size="small"
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${plant.latitude},${plant.longitude}`, '_blank')}
            >
              <Navigation />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {draftRestored && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDraftRestored(false)}>
            Bozza precedente ripristinata.
          </Alert>
        )}

        {/* Auto-sync status indicator */}
        {isOnline && autoSyncStatus === 'synced' && (
          <Alert severity="success" icon={<CloudDone fontSize="small" />} sx={{ mb: 2, py: 0 }}>
            Dati trasmessi automaticamente al server
          </Alert>
        )}
        {isOnline && autoSyncStatus === 'syncing' && (
          <Alert severity="info" icon={<CloudUpload fontSize="small" />} sx={{ mb: 2, py: 0 }}>
            Trasmissione in corso...
          </Alert>
        )}
        {isOnline && autoSyncStatus === 'error' && (
          <Alert severity="warning" sx={{ mb: 2, py: 0 }}>
            Errore trasmissione — i dati sono salvati localmente
          </Alert>
        )}

        {/* Tank Level */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <TankLevelInput value={tankLevel} onChange={setTankLevel} />
          </CardContent>
        </Card>

        {/* Plant-level Photos */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <PhotoCapture
              photos={plantPhotos}
              onChange={setPlantPhotos}
              maxPhotos={5}
              label="Foto impianto"
            />
          </CardContent>
        </Card>

        {/* Plant-level intervention notes */}
        {interventionNotes.filter(n => n.plant_id && !n.meter_id).length > 0 && (
          <Card sx={{ mb: 2, borderRadius: 2, border: '2px solid #ed6c02' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#ed6c02' }}>
                Note intervento impianto
              </Typography>
              {interventionNotes.filter(n => n.plant_id && !n.meter_id).map(note => (
                <Alert
                  key={note.id}
                  severity="warning"
                  icon={<AssignmentLate fontSize="small" />}
                  sx={{ mb: 0.5, py: 0, '& .MuiAlert-message': { width: '100%' } }}
                  action={
                    <IconButton
                      size="small"
                      color="inherit"
                      title="Completa nota"
                      onClick={async () => {
                        try {
                          await pwaApi.completeInterventionNote(note.id);
                          setInterventionNotes(prev => prev.filter(n => n.id !== note.id));
                          setSuccess('Nota completata');
                        } catch (e) {
                          setError('Errore completamento nota');
                        }
                      }}
                    >
                      <CheckCircleOutline fontSize="small" />
                    </IconButton>
                  }
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {note.nota}
                  </Typography>
                </Alert>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Progress indicator + Sort */}
        {meters.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Contatori
              </Typography>
              <Chip
                size="small"
                label={`${filledCount}/${meters.length}`}
                color={filledCount === meters.length ? 'success' : 'default'}
                variant={filledCount === meters.length ? 'filled' : 'outlined'}
              />
            </Box>
            <IconButton
              size="small"
              onClick={(e) => setSortMenuAnchor(e.currentTarget)}
              sx={{ color: 'text.secondary' }}
            >
              <Sort fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={sortMenuAnchor}
              open={Boolean(sortMenuAnchor)}
              onClose={() => setSortMenuAnchor(null)}
            >
              <MenuItem selected={sortBy === 'progressivo'} onClick={() => { setSortBy('progressivo'); setSortMenuAnchor(null); }}>
                Progressivo
              </MenuItem>
              <MenuItem selected={sortBy === 'matricola'} onClick={() => { setSortBy('matricola'); setSortMenuAnchor(null); }}>
                Matricola
              </MenuItem>
              <MenuItem selected={sortBy === 'letto'} onClick={() => { setSortBy('letto'); setSortMenuAnchor(null); }}>
                Non letti prima
              </MenuItem>
            </Menu>
          </Box>
        )}

        {/* Meters */}
        {meters.length === 0 ? (
          <Alert severity="info">Nessun contatore trovato per questo impianto</Alert>
        ) : (
          sortedMeters.map(meter => {
            const r = readings[meter.id] || {};
            const consumption = getConsumption(meter, r.lettura);
            const meterNotes = interventionNotes.filter(n => n.meter_id === meter.id);
            const insoluto = meter.insoluto_cliente || 0;

            return (
              <Card key={meter.id} sx={{ 
                mb: 2, borderRadius: 2, 
                border: consumption?.anomaly ? '2px solid #ff9800' : undefined,
                borderLeft: isDone(meter.id) && !consumption?.anomaly ? '4px solid #4caf50' : undefined,
                bgcolor: isDone(meter.id) ? 'rgba(76, 175, 80, 0.04)' : undefined
              }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Meter header */}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => updateReading(meter.id, 'expanded', !r.expanded)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700, flexShrink: 0 }}>
                        {meter.sotto_zona || '-'}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {meter.meter_number || `#${meter.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                        {meter.customer ? `${meter.customer.nome || ''} ${meter.customer.cognome || ''}`.trim() : 'Senza cliente'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
                      {isDone(meter.id) && <CheckCircle color="success" fontSize="small" />}
                      {r.photos?.length > 0 && !r.lettura && <CameraAlt fontSize="small" sx={{ color: '#4caf50' }} />}
                      {r.expanded ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </Box>

                  {/* Last reading chip + insoluto */}
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {meter.ultima_lettura != null && (
                      <Chip size="small" label={`Ultima: ${Math.round(meter.ultima_lettura)} mc`} variant="outlined" sx={{ height: 22 }} />
                    )}
                    {meter.data_ultima_lettura && (
                      <Chip size="small" label={new Date(meter.data_ultima_lettura).toLocaleDateString('it-IT')} variant="outlined" sx={{ height: 22 }} />
                    )}
                    {insoluto > 0 && (
                      <Chip
                        size="small"
                        label={`Insoluto: €${insoluto.toFixed(2)}`}
                        sx={{ height: 22, bgcolor: insoluto > 100 ? '#d32f2f' : '#ed6c02', color: 'white', fontWeight: 600 }}
                      />
                    )}
                  </Box>

                  {/* Intervention notes */}
                  {meterNotes.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {meterNotes.map(note => (
                        <Alert
                          key={note.id}
                          severity="warning"
                          icon={<AssignmentLate fontSize="small" />}
                          sx={{ mb: 0.5, py: 0, '& .MuiAlert-message': { width: '100%' } }}
                          action={
                            <IconButton
                              size="small"
                              color="inherit"
                              title="Completa nota"
                              onClick={async () => {
                                try {
                                  await pwaApi.completeInterventionNote(note.id);
                                  setInterventionNotes(prev => prev.filter(n => n.id !== note.id));
                                  setSuccess('Nota completata');
                                } catch (e) {
                                  setError('Errore completamento nota');
                                }
                              }}
                            >
                              <CheckCircleOutline fontSize="small" />
                            </IconButton>
                          }
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {note.nota}
                          </Typography>
                        </Alert>
                      ))}
                    </Box>
                  )}

                  <Collapse in={r.expanded !== false}>
                    <Divider sx={{ my: 1.5 }} />

                    {/* Reading input + action icons row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <TextField
                        label="Lettura (mc)"
                        type="number"
                        value={r.lettura || ''}
                        onChange={(e) => updateReading(meter.id, 'lettura', e.target.value)}
                        size="small"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">mc</InputAdornment>,
                          inputProps: { step: 0.01, min: 0, inputMode: 'decimal' }
                        }}
                        sx={{ flex: '0 0 60%' }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                        {/* Photo icon */}
                        {(r.photos || []).length < 5 && (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                document.getElementById(`meter-photo-input-${meter.id}`)?.click();
                              }}
                            >
                              <CameraAlt fontSize="small" />
                            </IconButton>
                            <input
                              id={`meter-photo-input-${meter.id}`}
                              type="file"
                              accept="image/*"
                              capture="environment"
                              multiple
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const files = Array.from(e.target.files);
                                if (!files.length) return;
                                const currentPhotos = r.photos || [];
                                const newPhotos = files.slice(0, 5 - currentPhotos.length).map(file => ({
                                  file,
                                  preview: URL.createObjectURL(file),
                                  name: file.name
                                }));
                                updateReading(meter.id, 'photos', [...currentPhotos, ...newPhotos]);
                                e.target.value = '';
                              }}
                            />
                          </>
                        )}

                        {/* Notes icon */}
                        <Tooltip title={r.note ? 'Modifica nota' : 'Aggiungi nota'}>
                          <IconButton
                            size="small"
                            color={r.note ? 'primary' : 'default'}
                            onClick={(e) => { e.stopPropagation(); setNoteDialog({ open: true, meterId: meter.id }); }}
                          >
                            <NoteAdd fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* History icon */}
                        {meter.history?.length > 0 && (
                          <Tooltip title="Ultime letture">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setHistoryAnchor(e.currentTarget); setHistoryMeterId(meter.id); }}
                            >
                              <History fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* Photo grid below */}
                    {(r.photos || []).length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {(r.photos || []).map((photo, idx) => (
                            <Box key={idx} sx={{ position: 'relative', width: 72, height: 72, borderRadius: 1, overflow: 'hidden' }}>
                              <img
                                src={photo.preview || photo.url}
                                alt={photo.name || `Foto ${idx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <IconButton
                                size="small"
                                sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', p: 0.3, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                onClick={() => {
                                  const updated = [...(r.photos || [])];
                                  if (updated[idx].preview) URL.revokeObjectURL(updated[idx].preview);
                                  updated.splice(idx, 1);
                                  updateReading(meter.id, 'photos', updated);
                                }}
                              >
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, lineHeight: 1 }}>✕</Typography>
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Consumption preview */}
                    {consumption && (
                      <Alert
                        severity={consumption.anomaly ? 'warning' : 'info'}
                        icon={consumption.anomaly ? <Warning /> : undefined}
                        sx={{ mb: 1, py: 0 }}
                      >
                        <Typography variant="body2">
                          Consumo: <strong>{consumption.mc} mc</strong> = <strong>{consumption.litri} L</strong>
                          {consumption.anomaly && ' ⚠ Anomalia'}
                        </Typography>
                      </Alert>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            );
          })
        )}

        {/* Save button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : (isOnline && autoSyncStatus === 'synced') ? <CloudDone /> : <Save />}
          color={(isOnline && autoSyncStatus === 'synced') ? 'success' : 'primary'}
          sx={{
            py: 1.5, borderRadius: 2, fontWeight: 600,
            position: 'sticky', bottom: 72, zIndex: 10,
            boxShadow: 4
          }}
        >
          {saving ? 'Salvataggio...' : isOnline ? (autoSyncStatus === 'synced' ? 'Completato — Torna alla lista' : `Concludi e torna alla lista`) : (!canSave ? 'Niente da salvare' : `Salva offline ${filledCount} lettur${filledCount === 1 ? 'a' : 'e'}`)}
        </Button>
      </Box>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        message={success}
      />

      {/* Notes dialog */}
      <Dialog
        open={noteDialog.open}
        onClose={() => setNoteDialog({ open: false, meterId: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Note lettura</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            value={noteDialog.meterId ? (readings[noteDialog.meterId]?.note || '') : ''}
            onChange={(e) => noteDialog.meterId && updateReading(noteDialog.meterId, 'note', e.target.value)}
            placeholder="Inserisci note per questa lettura..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, meterId: null })} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* History popover */}
      <Popover
        open={Boolean(historyAnchor)}
        anchorEl={historyAnchor}
        onClose={() => { setHistoryAnchor(null); setHistoryMeterId(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 240 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Ultime 3 letture</Typography>
          <Table size="small">
            <TableBody>
              {historyMeterId && (meters.find(m => m.id === historyMeterId)?.history || []).slice(0, 3).map((h, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ py: 0.3, px: 0.5, fontSize: '0.75rem', border: 0 }}>
                    {h.data_lettura ? new Date(h.data_lettura).toLocaleDateString('it-IT') : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.3, px: 0.5, fontSize: '0.75rem', fontWeight: 600, border: 0 }}>
                    {h.lettura_mc != null ? `${h.lettura_mc} mc` : '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.3, px: 0.5, fontSize: '0.75rem', color: 'text.secondary', border: 0 }}>
                    {h.consumo_litri != null ? `${h.consumo_litri} L` : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Popover>
    </Box>
  );
}
